import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Calendar, Check, X, Clock, MapPin, ChevronDown, ChevronUp, FileText, ExternalLink, CalendarPlus, ArrowDownToLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navigation from "@/components/Navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Build an ICS (iCalendar) file for an event and trigger download.
// Works with Apple Calendar, Google Calendar, and Outlook (one tap on mobile opens the calendar app).
const addToCalendar = (event, crewCallTime) => {
  const pad = (n) => String(n).padStart(2, '0');
  const dateStr = event.date; // YYYY-MM-DD
  if (!dateStr) return;

  // Start at the crew call time (when they need to arrive); fall back to show start time.
  const startT = crewCallTime || event.start_time || '09:00';
  const [sh, sm] = startT.split(':').map(Number);

  // End at the event end time, else 2 hours after start.
  let eh, em;
  if (event.end_time) {
    [eh, em] = event.end_time.split(':').map(Number);
  } else {
    eh = (sh + 2) % 24;
    em = sm;
  }

  const [y, mo, d] = dateStr.split('-').map(Number);
  const fmt = (hh, mm) => `${y}${pad(mo)}${pad(d)}T${pad(hh)}${pad(mm)}00`;
  const dtStart = fmt(sh, sm);
  const dtEnd = fmt(eh, em);

  const escape = (s) => (s || '').replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
  const summary = escape(event.name || 'Crew Call Event');
  const location = escape(event.location || '');
  const avParts = [];
  if (event.livestream_youtube) avParts.push('LIVESTREAM (YouTube)');
  if (event.recording_required) avParts.push('RECORDING');
  const descLines = [
    `Crew Call: ${formatTime12h(crewCallTime) || 'TBD'}`,
    event.start_time ? `Show: ${formatTime12h(event.start_time)}` : '',
    avParts.length ? `AV: ${avParts.join(' + ')}` : '',
    event.description ? `Notes: ${event.description}` : '',
  ].filter(Boolean);
  const description = escape(descLines.join('\n'));

  const uid = `${event.id || Math.random().toString(36).slice(2)}@versocrewcall`;
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//VERSO Crew Call//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTART:${dtStart}`,
    `DTEND:${dtEnd}`,
    `SUMMARY:${summary}`,
    description ? `DESCRIPTION:${description}` : '',
    location ? `LOCATION:${location}` : '',
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean).join('\r\n');

  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(event.name || 'event').replace(/[^a-z0-9]+/gi, '_').slice(0, 40)}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

// Convert 24-hour time to 12-hour AM/PM format
const formatTime12h = (time24) => {
  if (!time24) return null;
  try {
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  } catch {
    return time24;
  }
};

// Convert 12-hour time (e.g., "2:00 PM", "10:30 AM") to 24-hour format
const convertTo24Hour = (time12h) => {
  if (!time12h) return null;
  try {
    // Match patterns like "2:00 PM", "10:30 AM", "2 PM", "10AM"
    const match = time12h.match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM|am|pm)/i);
    if (!match) return null;
    
    let hours = parseInt(match[1], 10);
    const minutes = match[2] ? parseInt(match[2], 10) : 0;
    const period = match[3].toUpperCase();
    
    if (period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  } catch {
    return null;
  }
};

// Extract start time from notes (looks for "Program start time:" or "Event Time On Web:")
const extractStartTimeFromNotes = (notes) => {
  if (!notes) return null;
  
  // Pattern 1: "Program start time: X:XX" or "Program start time: X:XX PM"
  const programMatch = notes.match(/Program start time[:\s]+(\d{1,2}(?::\d{2})?\s*(?:AM|PM)?)/i);
  if (programMatch) {
    const timeStr = programMatch[1];
    // If it already has AM/PM, convert it
    if (/AM|PM/i.test(timeStr)) {
      return convertTo24Hour(timeStr);
    }
    // If no AM/PM, assume it's already in a usable format or needs context
    return timeStr.includes(':') ? timeStr : `${timeStr}:00`;
  }
  
  // Pattern 2: "Event Time On Web: X - Y PM" or "Event Time On Web: X:XX - Y:XX PM"
  const webMatch = notes.match(/Event Time On Web[:\s]+(\d{1,2}(?::\d{2})?)\s*(?:-|to)\s*(\d{1,2}(?::\d{2})?)\s*(AM|PM)/i);
  if (webMatch) {
    const startHour = webMatch[1];
    const period = webMatch[3];
    const startTimeStr = startHour.includes(':') ? `${startHour} ${period}` : `${startHour}:00 ${period}`;
    return convertTo24Hour(startTimeStr);
  }
  
  // Pattern 3: Simple "Event Time On Web: X PM" or "Event Time On Web: X:XX PM"
  const simpleWebMatch = notes.match(/Event Time On Web[:\s]+(\d{1,2}(?::\d{2})?\s*(?:AM|PM))/i);
  if (simpleWebMatch) {
    return convertTo24Hour(simpleWebMatch[1]);
  }
  
  return null;
};

// Get the effective start time (from notes if available, otherwise from event data)
const getEffectiveStartTime = (event) => {
  const notesStartTime = extractStartTimeFromNotes(event.description || event.notes);
  return notesStartTime || event.start_time;
};

// Calculate crew call time (1 hour before start time)
const calculateCallTime = (startTime, existingCallTime, notes) => {
  // First check if there's a start time in the notes
  const notesStartTime = extractStartTimeFromNotes(notes);
  const effectiveStartTime = notesStartTime || startTime;
  
  // If call_time is already set and different from start_time, use it (unless we found a notes time)
  if (!notesStartTime && existingCallTime && existingCallTime !== startTime) {
    return existingCallTime;
  }
  
  // Calculate 1 hour before the effective start time
  if (!effectiveStartTime) return null;
  try {
    const [hours, minutes] = effectiveStartTime.split(':').map(Number);
    const callHours = hours - 1;
    if (callHours < 0) return effectiveStartTime; // Edge case: if start time is before 1am
    return `${callHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  } catch {
    return effectiveStartTime;
  }
};

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

// Check if date is today
const isToday = (dateStr) => {
  if (!dateStr) return false;
  const today = new Date().toISOString().split('T')[0];
  return dateStr === today;
};

// Check if date is in the past
const isPast = (dateStr) => {
  if (!dateStr) return false;
  const today = new Date().toISOString().split('T')[0];
  return dateStr < today;
};

const CrewDashboard = () => {
  const navigate = useNavigate();
  const [crewData, setCrewData] = useState(null);
  const [events, setEvents] = useState([]);
  const [goToSearch, setGoToSearch] = useState('');
  const [myAttendance, setMyAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedNotes, setExpandedNotes] = useState({});

  // Toggle notes expansion for an event
  const toggleNotes = (eventId) => {
    setExpandedNotes(prev => ({
      ...prev,
      [eventId]: !prev[eventId]
    }));
  };

  // Reusable Notes Dropdown component
  const NotesDropdown = ({ eventId, notes, isCancelled }) => {
    if (!notes) return null;
    const isExpanded = expandedNotes[eventId];
    return (
      <div className="mt-2">
        <button
          onClick={() => toggleNotes(eventId)}
          className={`flex items-center gap-2 text-sm ${isCancelled ? 'text-zinc-500' : 'text-zinc-400 hover:text-crew-yellow'} transition-colors`}
        >
          <FileText className="w-4 h-4" />
          <span>Other Notes</span>
          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {isExpanded && (
          <div className={`mt-2 p-3 rounded-sm text-sm ${isCancelled ? 'bg-zinc-800/30 text-zinc-500' : 'bg-zinc-800/50 text-zinc-300'}`}>
            {notes}
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    // Check if crew is authenticated
    const token = localStorage.getItem('crewToken');
    const data = localStorage.getItem('crewData');
    
    if (!token || !data) {
      toast.error("Please login first");
      navigate('/crew/login');
      return;
    }

    setCrewData(JSON.parse(data));
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('crewToken');
      const userData = JSON.parse(localStorage.getItem('crewData'));
      const config = {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      };

      const [eventsRes, attendanceRes] = await Promise.all([
        axios.get(`${API}/events`, { withCredentials: true }),
        axios.get(`${API}/attendance/participant/${userData.participant_id}`, config)
      ]);

      setEvents(eventsRes.data);
      setMyAttendance(attendanceRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        localStorage.removeItem('crewToken');
        localStorage.removeItem('crewData');
        navigate('/crew/login');
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAttendance = async (eventId) => {
    try {
      const userData = JSON.parse(localStorage.getItem('crewData'));
      const token = localStorage.getItem('crewToken');
      
      await axios.post(`${API}/attendance`, {
        participant_id: userData.participant_id,
        event_id: eventId
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      toast.success("Request received, thank you!");
      loadData();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("Already confirmed");
      } else {
        toast.error(error.response?.data?.detail || "Failed to confirm attendance");
      }
    }
  };

  const handleRemoveAttendance = async (eventId) => {
    try {
      const userData = JSON.parse(localStorage.getItem('crewData'));
      const token = localStorage.getItem('crewToken');
      
      await axios.delete(`${API}/attendance/${userData.participant_id}/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      toast.success("Attendance removed");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove attendance");
    }
  };

  const isAttending = (eventId) => {
    return myAttendance.some(att => att.event.id === eventId);
  };

  // Scroll the All Upcoming list to the first event dated today or the nearest upcoming date.
  const handleJumpToToday = () => {
    const todayStr = new Date().toISOString().split('T')[0];
    // upcomingEvents is date-sorted; find first event on/after today (all are upcoming, so first match works)
    const targetIdx = upcomingEvents.findIndex(e => (e.date || '') >= todayStr);
    const idx = targetIdx === -1 ? 0 : targetIdx;
    const el = document.getElementById(`upcoming-card-${idx}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.classList.add('ring-2', 'ring-crew-yellow');
      setTimeout(() => el.classList.remove('ring-2', 'ring-crew-yellow'), 1800);
    } else {
      toast.info("No upcoming events to jump to");
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('crewToken');
      await axios.post(`${API}/crew/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
    } catch (error) {
      // Ignore errors, still clear local storage
    }
    localStorage.removeItem('crewToken');
    localStorage.removeItem('crewData');
    toast.success("Logged out successfully");
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-crew-black flex items-center justify-center">
        <div className="text-zinc-500 text-xl">Loading dashboard...</div>
      </div>
    );
  }

  // Filter confirmed attendance - only approved and not past
  const upcomingConfirmed = myAttendance.filter(item => 
    (item.status === 'approved' || !item.status) && !isPast(item.event.date)
  );
  const pastConfirmed = myAttendance.filter(item => isPast(item.event.date));

  // Map each event id to this crew member's attendance status (for the full listing)
  const attendanceStatusByEventId = {};
  myAttendance.forEach(item => {
    if (item.event && item.event.id) {
      attendanceStatusByEventId[item.event.id] = item.status || 'approved';
    }
  });

  // Full date-sorted listing of ALL upcoming events (includes confirmed & pending ones)
  const _goToQ = goToSearch.trim().toLowerCase();
  const upcomingEvents = events
    .filter(event => 
      !isPast(event.date) &&
      (!_goToQ || (event.name || '').toLowerCase().includes(_goToQ) || (event.date || '').includes(_goToQ))
    )
    .sort((a, b) => {
      const dateCmp = (a.date || '').localeCompare(b.date || '');
      if (dateCmp !== 0) return dateCmp;
      return (a.start_time || a.call_time || '').localeCompare(b.start_time || b.call_time || '');
    });

  return (
    <div className="min-h-screen bg-crew-black relative grain-overlay">
      <Navigation 
        userType="crew" 
        userName={crewData?.preferred_name || crewData?.name || "Crew Member"} 
      />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 max-w-6xl flex flex-col">
        {/* Pending Requests */}
        {myAttendance.filter(item => item.status === 'pending').length > 0 && (
          <div className="mb-12 order-2">
            <h2 className="text-2xl font-bold text-yellow-400 mb-6 flex items-center gap-2">
              <Clock className="w-6 h-6" /> EVENTS REQUESTED: PENDING CONFIRMATION ({myAttendance.filter(item => item.status === 'pending').length})
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {myAttendance.filter(item => item.status === 'pending' && !isPast(item.event.date)).map((item, idx) => {
                const isCancelled = item.event.status === 'cancelled';
                const effectiveStartTime = getEffectiveStartTime(item.event);
                const crewCallTime = calculateCallTime(item.event.start_time, item.event.call_time, item.event.description);
                return (
                <div
                  key={item.event?.id || idx}
                  className={`bg-crew-zinc border p-6 rounded-sm relative overflow-hidden ${isCancelled ? 'border-red-500/50 opacity-70' : 'border-yellow-500/50'}`}
                  data-testid={`pending-event-${idx}`}
                >
                  <div className={`absolute top-2 right-2 text-xs font-bold px-2 py-1 rounded-sm ${isCancelled ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                    {isCancelled ? 'EVENT CANCELLED' : 'PENDING CONFIRMATION'}
                  </div>
                  
                  <h3 className={`text-xl font-bold mb-3 mt-4 ${isCancelled ? 'text-red-400 line-through' : 'text-white'}`}>
                    {item.event.name}
                  </h3>
                  
                  <div className="space-y-2 text-sm mb-4">
                    <div className={`flex items-center gap-2 ${isCancelled ? 'text-zinc-500 line-through' : 'text-zinc-300'}`}>
                      <Calendar className={`w-4 h-4 ${isCancelled ? 'text-zinc-500' : 'text-yellow-400'}`} />
                      <span className="font-mono">{formatDate(item.event.date)}</span>
                    </div>
                    <div className={`flex items-center gap-2 ${isCancelled ? 'text-zinc-500 line-through' : 'text-zinc-200'}`}>
                      <Clock className={`w-4 h-4 ${isCancelled ? 'text-zinc-500' : 'text-yellow-400'}`} />
                      <div className="font-mono">
                        <span className="text-yellow-400 font-bold">CREW CALL: {formatTime12h(crewCallTime) || '—'}</span>
                        {effectiveStartTime && (
                          <span className="text-zinc-400 ml-3">
                            Show: {formatTime12h(effectiveStartTime)}
                          </span>
                        )}
                      </div>
                    </div>
                    {item.event.location && (
                      <div className={`flex items-center gap-2 ${isCancelled ? 'text-zinc-500 line-through' : 'text-zinc-300'}`}>
                        <MapPin className={`w-4 h-4 ${isCancelled ? 'text-zinc-500' : 'text-yellow-400'}`} />
                        <span>{item.event.location}</span>
                      </div>
                    )}
                    {(item.event.livestream_youtube || item.event.recording_required) && !isCancelled && (
                      <div className="flex flex-wrap gap-2 pt-1" data-testid={`pending-av-badges-${idx}`}>
                        {item.event.livestream_youtube && (
                          <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded bg-red-500/20 text-red-300">● LIVESTREAM (YouTube)</span>
                        )}
                        {item.event.recording_required && (
                          <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded bg-blue-500/20 text-blue-300">● RECORDING</span>
                        )}
                      </div>
                    )}
                    <NotesDropdown eventId={`pending-${item.event.id}`} notes={item.event.description} isCancelled={isCancelled} />
                    {item.event.website_url && !isCancelled && (
                      <a 
                        href={item.event.website_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors mt-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="w-4 h-4" />
                        <span>View on Library Website</span>
                      </a>
                    )}
                  </div>
                  
                  <p className="text-sm text-zinc-400">
                    {isCancelled ? 'This event has been cancelled.' : 'Your request is being reviewed by an administrator.'}
                  </p>
                  
                  <Button
                    onClick={() => handleRemoveAttendance(item.event.id)}
                    variant="outline"
                    size="sm"
                    className="mt-4 border-zinc-700 text-zinc-300 hover:border-red-500 hover:text-red-500"
                  >
                    <X className="mr-2 h-4 w-4" /> Cancel Request
                  </Button>
                </div>
              );
              })}
            </div>
          </div>
        )}

        {/* Confirmed Events */}
        <div className="mb-12 order-1">
          <h2 className="text-2xl font-bold text-crew-yellow mb-6 flex items-center gap-2">
            <Check className="w-6 h-6" /> CONFIRMED ({upcomingConfirmed.length})
          </h2>
          
          {upcomingConfirmed.length === 0 ? (
            <div className="bg-crew-zinc border border-zinc-800 rounded-sm p-12 text-center text-zinc-400" data-testid="no-confirmed-events">
              You have no confirmed attendance for any upcoming events yet.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {upcomingConfirmed.map((item, idx) => {
                const eventIsToday = isToday(item.event.date);
                const isCancelled = item.event.status === 'cancelled';
                const effectiveStartTime = getEffectiveStartTime(item.event);
                const crewCallTime = calculateCallTime(item.event.start_time, item.event.call_time, item.event.description);
                return (
                  <div
                    key={item.event?.id || idx}
                    className={`bg-crew-zinc border p-6 rounded-sm relative overflow-hidden transition-colors ${
                      isCancelled ? 'border-red-500/50 opacity-70' : 
                      eventIsToday ? 'border-crew-yellow border-2' : 'border-zinc-800 hover:border-crew-yellow'
                    }`}
                    data-testid={`confirmed-event-${idx}`}
                  >
                    {eventIsToday && !isCancelled && (
                      <div className="absolute top-2 left-2 bg-crew-yellow text-black text-xs font-bold px-2 py-1 rounded-sm">
                        TODAY
                      </div>
                    )}
                    <div className={`absolute top-4 right-4 text-xs font-bold px-2 py-1 rounded-sm ${
                      isCancelled ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
                    }`}>
                      {isCancelled ? 'EVENT CANCELLED' : 'CONFIRMED'}
                    </div>
                    
                    <h3 className={`text-xl font-bold mb-3 mt-4 ${isCancelled ? 'text-red-400 line-through' : 'text-crew-yellow'}`}>
                      {item.event.name}
                    </h3>
                    
                    <div className="space-y-2 text-sm mb-4">
                      <div className={`flex items-center gap-2 ${isCancelled ? 'text-zinc-500 line-through' : 'text-zinc-300'}`}>
                        <Calendar className={`w-4 h-4 ${isCancelled ? 'text-zinc-500' : 'text-crew-yellow'}`} />
                        <span className="font-mono">{formatDate(item.event.date)}</span>
                      </div>
                      <div className={`flex items-center gap-2 ${isCancelled ? 'text-zinc-500 line-through' : 'text-white font-bold'}`}>
                        <Clock className={`w-4 h-4 ${isCancelled ? 'text-zinc-500' : 'text-crew-yellow'}`} />
                        <div className="font-mono">
                          <span className="text-crew-yellow font-bold">CREW CALL: {formatTime12h(crewCallTime) || '—'}</span>
                          {effectiveStartTime && (
                            <span className="text-zinc-400 font-normal ml-3">
                              Show: {formatTime12h(effectiveStartTime)}
                            </span>
                          )}
                        </div>
                      </div>
                      {item.event.location && (
                        <div className={`flex items-center gap-2 ${isCancelled ? 'text-zinc-500 line-through' : 'text-zinc-300'}`}>
                          <MapPin className={`w-4 h-4 ${isCancelled ? 'text-zinc-500' : 'text-crew-yellow'}`} />
                          <span>{item.event.location}</span>
                        </div>
                      )}
                      {(item.event.livestream_youtube || item.event.recording_required) && !isCancelled && (
                        <div className="flex flex-wrap gap-2 pt-1" data-testid={`confirmed-av-badges-${idx}`}>
                          {item.event.livestream_youtube && (
                            <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded bg-red-500/20 text-red-300">● LIVESTREAM (YouTube)</span>
                          )}
                          {item.event.recording_required && (
                            <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded bg-blue-500/20 text-blue-300">● RECORDING</span>
                          )}
                        </div>
                      )}
                      <NotesDropdown eventId={`confirmed-${item.event.id}`} notes={item.event.description} isCancelled={isCancelled} />
                      {item.event.website_url && !isCancelled && (
                        <a 
                          href={item.event.website_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors mt-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="w-4 h-4" />
                          <span>View on Library Website</span>
                        </a>
                      )}
                    </div>
                    
                    {isCancelled ? (
                      <p className="text-sm text-red-400/80">This event has been cancelled.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        <Button
                          onClick={() => addToCalendar(item.event, crewCallTime)}
                          size="sm"
                          className="bg-crew-yellow text-black font-bold hover:bg-white"
                          data-testid={`add-to-calendar-${idx}`}
                        >
                          <CalendarPlus className="mr-2 h-4 w-4" /> Add to Calendar
                        </Button>
                        <Button
                          onClick={() => handleRemoveAttendance(item.event.id)}
                          variant="outline"
                          size="sm"
                          className="border-zinc-700 text-zinc-300 hover:border-red-500 hover:text-red-500"
                          data-testid={`remove-attendance-${idx}`}
                        >
                          <X className="mr-2 h-4 w-4" /> Remove Attendance
                        </Button>
                      </div>
                    )}
                </div>
              );
              })}
            </div>
          )}
        </div>

        {/* All Upcoming Events */}
        <div className="order-3">
          <h2 className="text-2xl font-bold text-crew-yellow mb-3 flex items-center gap-2">
            <Calendar className="w-6 h-6" /> ALL UPCOMING EVENTS ({upcomingEvents.length})
          </h2>

          {/* Status legend */}
          <div className="flex flex-wrap items-center gap-4 mb-4 text-xs" data-testid="status-legend">
            <span className="flex items-center gap-1.5 text-zinc-400">
              <span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" /> Confirmed
            </span>
            <span className="flex items-center gap-1.5 text-zinc-400">
              <span className="w-2.5 h-2.5 rounded-full bg-yellow-500 inline-block" /> Pending
            </span>
            <span className="flex items-center gap-1.5 text-zinc-400">
              <span className="w-2.5 h-2.5 rounded-full bg-zinc-600 inline-block" /> Not requested
            </span>
          </div>

          <div className="mb-6 flex flex-col sm:flex-row gap-2">
            <input
              value={goToSearch}
              onChange={(e) => setGoToSearch(e.target.value)}
              placeholder="Go to… type an event name or date (e.g. 2026-08-15)"
              className="flex-1 bg-crew-zinc border border-zinc-700 text-white rounded-sm h-11 px-3 text-base focus:border-crew-yellow focus:outline-none"
              data-testid="crew-goto-search"
            />
            <Button
              type="button"
              onClick={handleJumpToToday}
              variant="outline"
              className="h-11 border-crew-yellow text-crew-yellow hover:bg-crew-yellow hover:text-black whitespace-nowrap"
              data-testid="jump-to-today-btn"
            >
              <ArrowDownToLine className="mr-2 h-4 w-4" /> Jump to Today
            </Button>
          </div>

          {upcomingEvents.length === 0 ? (
            <div className="bg-crew-zinc border border-zinc-800 rounded-sm p-12 text-center text-zinc-400" data-testid="no-events">
              No upcoming events found{goToSearch ? ' for your search' : ''}.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {upcomingEvents.map((event, idx) => {
                const eventIsToday = isToday(event.date);
                const isCancelled = event.status === 'cancelled';
                const effectiveStartTime = getEffectiveStartTime(event);
                const crewCallTime = calculateCallTime(event.start_time, event.call_time, event.description);
                const myStatus = attendanceStatusByEventId[event.id]; // 'approved' | 'pending' | undefined
                const isConfirmed = myStatus === 'approved' || (myStatus && myStatus !== 'pending');
                const isPending = myStatus === 'pending';
                
                return (
                  <div
                    key={event.id}
                    id={`upcoming-card-${idx}`}
                    data-date={event.date}
                    className={`bg-crew-zinc border p-6 rounded-sm relative overflow-hidden transition-colors scroll-mt-24 ${
                      isCancelled ? 'border-red-500/50 opacity-70' :
                      isConfirmed ? 'border-green-600/60' :
                      isPending ? 'border-yellow-500/50' :
                      eventIsToday ? 'border-crew-yellow' : 'border-zinc-800 hover:border-zinc-600'
                    }`}
                    data-testid={`event-${idx}`}
                  >
                    {isCancelled && (
                      <div className="absolute top-2 right-2 bg-red-500/20 text-red-400 text-xs font-bold px-2 py-1 rounded-sm">
                        CANCELLED
                      </div>
                    )}
                    {!isCancelled && isConfirmed && (
                      <div className="absolute top-2 right-2 bg-green-500/20 text-green-400 text-xs font-bold px-2 py-1 rounded-sm" data-testid={`list-status-confirmed-${idx}`}>
                        CONFIRMED
                      </div>
                    )}
                    {!isCancelled && isPending && (
                      <div className="absolute top-2 right-2 bg-yellow-500/20 text-yellow-400 text-xs font-bold px-2 py-1 rounded-sm" data-testid={`list-status-pending-${idx}`}>
                        PENDING
                      </div>
                    )}
                    {eventIsToday && !isCancelled && (
                      <div className="absolute top-2 left-2 bg-crew-yellow text-black text-xs font-bold px-2 py-1 rounded-sm">
                        TODAY
                      </div>
                    )}
                    
                    <h3 className={`text-xl font-bold mb-3 ${eventIsToday || isCancelled || isConfirmed || isPending ? 'mt-4' : ''} ${isCancelled ? 'text-red-400 line-through' : 'text-white'}`}>
                      {event.name}
                    </h3>
                    
                    <div className="space-y-2 text-sm mb-4">
                      <div className={`flex items-center gap-2 ${isCancelled ? 'text-zinc-500 line-through' : 'text-zinc-300'}`}>
                        <Calendar className={`w-4 h-4 ${isCancelled ? 'text-zinc-500' : 'text-crew-yellow'}`} />
                        <span className="font-mono">{formatDate(event.date)}</span>
                      </div>
                      <div className={`flex items-center gap-2 ${isCancelled ? 'text-zinc-500 line-through' : 'text-white font-bold'}`}>
                        <Clock className={`w-4 h-4 ${isCancelled ? 'text-zinc-500' : 'text-crew-yellow'}`} />
                        <div className="font-mono">
                          <span className="text-crew-yellow font-bold">CREW CALL: {formatTime12h(crewCallTime) || '—'}</span>
                          {effectiveStartTime && (
                            <span className="text-zinc-400 font-normal ml-3">
                              Show: {formatTime12h(effectiveStartTime)}
                            </span>
                          )}
                        </div>
                      </div>
                      {event.location && (
                        <div className={`flex items-center gap-2 ${isCancelled ? 'text-zinc-500 line-through' : 'text-zinc-300'}`}>
                          <MapPin className={`w-4 h-4 ${isCancelled ? 'text-zinc-500' : 'text-crew-yellow'}`} />
                          <span>{event.location}</span>
                        </div>
                      )}
                      {(event.livestream_youtube || event.recording_required) && !isCancelled && (
                        <div className="flex flex-wrap gap-2 pt-1" data-testid={`av-badges-${idx}`}>
                          {event.livestream_youtube && (
                            <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded bg-red-500/20 text-red-300">● LIVESTREAM (YouTube)</span>
                          )}
                          {event.recording_required && (
                            <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded bg-blue-500/20 text-blue-300">● RECORDING</span>
                          )}
                        </div>
                      )}
                      <NotesDropdown eventId={`upcoming-${event.id}`} notes={event.description} isCancelled={isCancelled} />
                      {event.website_url && !isCancelled && (
                        <a 
                          href={event.website_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors mt-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="w-4 h-4" />
                          <span>View on Library Website</span>
                        </a>
                      )}
                    </div>
                    
                    {!isCancelled && !isConfirmed && !isPending && (
                      <div className="mb-2" data-testid={`signup-status-${idx}`}>
                        {event.signups_available === false
                          ? <span className="text-xs font-bold uppercase text-zinc-400">● Sign-ups Closed</span>
                          : <span className="text-xs font-bold uppercase text-green-400">● Sign-ups Open</span>}
                      </div>
                    )}
                    {isCancelled ? (
                      <p className="text-sm text-red-400/80">This event has been cancelled.</p>
                    ) : isConfirmed ? (
                      <div className="flex flex-wrap gap-2">
                        <Button
                          onClick={() => addToCalendar(event, crewCallTime)}
                          size="sm"
                          className="bg-crew-yellow text-black font-bold hover:bg-white"
                          data-testid={`list-add-to-calendar-${idx}`}
                        >
                          <CalendarPlus className="mr-2 h-4 w-4" /> Add to Calendar
                        </Button>
                        <Button
                          onClick={() => handleRemoveAttendance(event.id)}
                          variant="outline"
                          size="sm"
                          className="border-zinc-700 text-zinc-300 hover:border-red-500 hover:text-red-500"
                          data-testid={`list-remove-attendance-${idx}`}
                        >
                          <X className="mr-2 h-4 w-4" /> Remove Attendance
                        </Button>
                      </div>
                    ) : isPending ? (
                      <Button
                        onClick={() => handleRemoveAttendance(event.id)}
                        variant="outline"
                        size="sm"
                        className="border-zinc-700 text-zinc-300 hover:border-red-500 hover:text-red-500"
                        data-testid={`list-cancel-request-${idx}`}
                      >
                        <X className="mr-2 h-4 w-4" /> Cancel Request
                      </Button>
                    ) : event.signups_available === false ? (
                      <div className="flex items-center gap-2" data-testid={`signup-closed-${idx}`}>
                        <span className="px-3 py-1 bg-zinc-700 text-zinc-300 text-xs font-bold uppercase rounded">Sign-ups Closed</span>
                        <span className="text-xs text-zinc-500">This event is full or closed for now.</span>
                      </div>
                    ) : (
                      <Button
                        onClick={() => handleConfirmAttendance(event.id)}
                        className="bg-crew-yellow text-black font-bold hover:bg-white"
                        data-testid={`request-attendance-${idx}`}
                      >
                        <Check className="mr-2 h-4 w-4" /> Request to Attend
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CrewDashboard;
